import { ChevronDownIcon, ChevronRightIcon, CircleHelpIcon, ExternalLinkIcon, LoaderCircleIcon, ScaleIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import type { CodeCandidate, DecisionRef, GoodsProfile, SessionDetail } from "../../../../shared/api.ts";
import { formatCode } from "../../../../shared/uktzed.ts";
import { Code, CopyButton } from "@/components/common/code.tsx";
import { MarkdownText } from "@/components/common/markdown.tsx";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible.tsx";
import { TabCount } from "@/components/ui/tabs.tsx";
import { CodeAnatomy } from "@/components/uktzed/code-anatomy.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { cn } from "@/lib/utils.ts";
import { Box31Block } from "./box31.tsx";
import { CitationQuote, FactSources } from "./citations.tsx";
import { DeclareCodeAction, DeclaredCodeProvider, DeclaredCodeSection, useIsDeclared } from "./declared-code.tsx";
import { FeedbackSection } from "./feedback.tsx";
import { collectImages } from "./images-tab.tsx";
import { Lightbox } from "./lightbox.tsx";
import { ConfidenceMeter, PanelSection } from "./section.tsx";

const FACTS: Array<keyof GoodsProfile> = ["articleNumber", "brand", "manufacturer", "shape", "materials", "originCountry", "description", "other"];

/** "Not found" duty reads "Не знайдено" in Ukrainian. */
export function useDutyText() {
  const { t } = useI18n();
  return (duty: string) => (duty === "Not found" || duty === "Не знайдено" ? t.common.notFound : duty);
}

/** How many pictures of the good the section shows: one row. The rest are in the Images tab. */
const PREVIEW_PICTURES = 3;

function GoodSection({ detail, onShowImages }: { detail: SessionDetail; onShowImages?: () => void }) {
  const { t } = useI18n();
  const [lightbox, setLightbox] = useState<number | null>(null);
  const good = detail.findings?.good ?? null;
  const pictures = detail.pictures;
  const preview = pictures.slice(0, PREVIEW_PICTURES);
  const images = collectImages(detail).length;
  const facts = good ? FACTS.filter((key) => key !== "name" && good[key]) : [];
  const evidence = detail.findings?.evidence;
  if (!good && pictures.length === 0) return null;

  return (
    <PanelSection
      title={t.panel.good}
      aside={
        onShowImages && images > preview.length ? (
          <button
            type="button"
            onClick={onShowImages}
            className="inline-flex items-center gap-0.5 rounded-sm text-[12.5px] font-semibold text-primary hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/35 focus-visible:outline-none"
          >
            {t.panel.allImages(images)}
            <ChevronRightIcon className="size-3.5" aria-hidden />
          </button>
        ) : null
      }
    >
      {preview.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {preview.map((picture, index) => (
            <button
              key={picture.id}
              type="button"
              onClick={() => setLightbox(index)}
              aria-label={`${t.panel.pictureOpen}: ${picture.caption || t.panel.pictureAlt(index + 1)}`}
              className="group relative aspect-square overflow-hidden rounded-lg border bg-muted transition-[border-color,box-shadow] hover:border-primary/40 focus-visible:ring-[3px] focus-visible:ring-ring/35 focus-visible:outline-none"
            >
              <img src={picture.url} alt="" loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
            </button>
          ))}
        </div>
      ) : null}
      {good?.name ? <p className="text-[15px] leading-snug font-bold tracking-[-0.01em]">{good.name}</p> : null}
      {facts.length > 0 ? (
        <dl className="grid grid-cols-[minmax(5.5rem,auto)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[13px]">
          {facts.map((key) => {
            const label = t.panel.facts[key as Exclude<keyof GoodsProfile, "name">];
            const entry = evidence?.find((item) => item.field === key);
            return (
              <div key={key} className="contents">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="min-w-0 break-words">
                  <span className={cn(key === "articleNumber" && "code-mono")}>{good?.[key]}</span>
                  {entry ? (
                    <span className="mt-0.5 flex">
                      <FactSources entry={entry} label={label} />
                    </span>
                  ) : null}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : null}
      <Lightbox pictures={pictures} index={lightbox} onIndexChange={setLightbox} onClose={() => setLightbox(null)} />
    </PanelSection>
  );
}

/**
 * Decisions or BTI rulings, each with its summary and, below it, the verified quote from the ruling or the page the
 * decision was read from, with that page under it; a ruling's reference already links to the ruling. A decision
 * stored before decisions carried quotes shows without one.
 */
function DecisionList({ items, empty }: { items: DecisionRef[]; empty: string }) {
  if (items.length === 0) return <p className="text-[12.5px] text-muted-foreground">{empty}</p>;
  return (
    <ul className="flex flex-col gap-3">
      {items.map((item) => (
        <li key={item.reference} className="text-[12.5px]">
          {item.url ? (
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
              <span className="code-mono">{item.reference}</span>
              <ExternalLinkIcon className="size-3" aria-hidden />
            </a>
          ) : (
            <span className="code-mono font-semibold">{item.reference}</span>
          )}
          <p className="mt-0.5 leading-relaxed text-muted-foreground">{item.summary}</p>
          {item.citation ? <CitationQuote citation={item.citation} compact source={item.citation.kind !== "bti"} className="mt-1.5" /> : null}
        </li>
      ))}
    </ul>
  );
}

function ChosenCodeCard({ candidate, box31 }: { candidate: CodeCandidate; box31?: ReactNode }) {
  const { t } = useI18n();
  const duty = useDutyText();
  const declared = useIsDeclared(candidate.code);
  return (
    <section className={cn("overflow-hidden rounded-xl border bg-card shadow-[0_1px_3px_rgba(15,27,45,0.05)]", declared && "border-success/50")}>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase">{t.panel.chosenCode}</span>
          <Badge variant="duty">{t.panel.duty(duty(candidate.duty))}</Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="code-mono text-[26px] leading-none font-semibold tracking-[-0.01em] text-foreground">{formatCode(candidate.code)}</span>
          <CopyButton
            value={candidate.code}
            label={t.panel.copyCode}
            copiedLabel={t.panel.codeCopied}
            size="icon-sm"
            onCopied={() => toast.success(t.panel.codeCopied, { description: formatCode(candidate.code) })}
          />
        </div>
        <CodeAnatomy code={candidate.code} title={candidate.title} />
        <ConfidenceMeter value={candidate.confidence} label={t.panel.confidence} />
        <DeclareCodeAction code={candidate.code} />
      </div>
      {candidate.reason ? (
        <div className="border-t px-4 py-3.5">
          <p className="mb-1.5 text-[12px] font-semibold text-muted-foreground">{t.panel.reason}</p>
          <MarkdownText text={candidate.reason} size="sm" />
        </div>
      ) : null}
      <Accordion type="multiple" className="border-t px-4">
        <AccordionItem value="ua">
          <AccordionTrigger>
            <ScaleIcon className="size-4 text-muted-foreground" aria-hidden />
            {t.panel.ukrainianDecisions}
            <TabCount value={candidate.ukrainianDecisions.length} />
          </AccordionTrigger>
          <AccordionContent>
            <DecisionList items={candidate.ukrainianDecisions} empty={t.panel.noUkrainianDecision} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="bti">
          <AccordionTrigger>
            <ScaleIcon className="size-4 text-muted-foreground" aria-hidden />
            {t.panel.europeanBti}
            <TabCount value={candidate.europeanBti.length} />
          </AccordionTrigger>
          <AccordionContent>
            <DecisionList items={candidate.europeanBti} empty={t.panel.noEuropeanBti} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {box31}
    </section>
  );
}

function OpenCodeCard({ candidate, box31 }: { candidate: CodeCandidate; box31?: ReactNode }) {
  const { t } = useI18n();
  const duty = useDutyText();
  const declared = useIsDeclared(candidate.code);
  return (
    <li className={cn("flex flex-col gap-2 rounded-xl border bg-card p-3.5", declared && "border-success/50 bg-success-soft/30")}>
      <div className="flex items-center justify-between gap-2">
        <Code value={candidate.code} className="text-[15px] font-semibold" />
        <ConfidenceMeter value={candidate.confidence} label={t.panel.confidence} compact />
      </div>
      <CodeAnatomy code={candidate.code} title={candidate.title} variant="compact" />
      {candidate.reason ? <MarkdownText text={candidate.reason} size="sm" className="text-muted-foreground" /> : null}
      <Badge variant="duty" className="text-[11px]">
        {t.panel.duty(duty(candidate.duty))}
      </Badge>
      <DeclareCodeAction code={candidate.code} className="mt-0.5" />
      {box31}
    </li>
  );
}

function ComparedCodes({ candidates, declaredCode }: { candidates: CodeCandidate[]; declaredCode: string | null }) {
  const { t } = useI18n();
  // A declared code among them stays in sight.
  const [open, setOpen] = useState(() => candidates.some((candidate) => candidate.code === declaredCode));
  if (candidates.length === 0) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-xl border bg-card">
      <CollapsibleTrigger className="group flex w-full items-center gap-2 px-3.5 py-3 text-left text-[13px] font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35">
        {t.panel.comparedCodes}
        <TabCount value={candidates.length} />
        <ChevronDownIcon className="ml-auto size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" aria-hidden />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="divide-y border-t">
          {candidates.map((candidate) => (
            <li key={candidate.code} className="flex flex-col gap-1.5 px-3.5 py-3">
              <div className="flex items-center justify-between gap-2">
                <Code value={candidate.code} className="text-[13.5px] font-semibold" />
                <span className="code-mono text-[11.5px] text-muted-foreground">{Math.round(candidate.confidence * 100)}%</span>
              </div>
              <p className="text-[12.5px] text-foreground/85">{candidate.title}</p>
              {candidate.reason ? (
                <div className="rounded-lg bg-muted/60 px-2.5 py-2">
                  <p className="mb-0.5 text-[11px] font-semibold text-muted-foreground">{t.panel.ruledOut}</p>
                  <MarkdownText text={candidate.reason} size="sm" className="text-muted-foreground" />
                </div>
              ) : null}
              <DeclareCodeAction code={candidate.code} ruledOutReason={candidate.reason} className="mt-0.5" />
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Good, open questions, the chosen code with duty and decisions, declared code, feedback, other codes.
 * Every code card can be picked as the declared code. `onShowImages` opens the Images tab of the panel.
 */
export function ClassificationTab({ detail, readOnly, onShowImages }: { detail: SessionDetail; readOnly: boolean; onShowImages?: () => void }) {
  return (
    <DeclaredCodeProvider sessionId={detail.session.id} declaredCode={detail.session.declaredCode} readOnly={readOnly}>
      <ClassificationContent detail={detail} readOnly={readOnly} onShowImages={onShowImages} />
    </DeclaredCodeProvider>
  );
}

function ClassificationContent({ detail, readOnly, onShowImages }: { detail: SessionDetail; readOnly: boolean; onShowImages?: () => void }) {
  const { t } = useI18n();
  const findings = detail.findings;
  const running = detail.activeTurnId !== null || detail.session.running;
  const candidates = findings?.candidates ?? [];
  const chosen = candidates.find((candidate) => candidate.outcome === "chosen") ?? null;
  const open = candidates.filter((candidate) => candidate.outcome === "open");
  const related = candidates.filter((candidate) => candidate.outcome === "related");
  const clarifying = findings?.phase === "clarifying";
  const hasGood = Boolean(findings?.good) || detail.pictures.length > 0;

  if (!findings && !hasGood) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <span className="flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
          {running ? <LoaderCircleIcon className="size-5 animate-spin text-primary" aria-hidden /> : <ScaleIcon className="size-5" aria-hidden />}
        </span>
        <p className="max-w-[18rem] text-[13px] leading-relaxed text-muted-foreground">{running ? t.panel.emptyRunning : t.panel.empty}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4">
      <GoodSection detail={detail} onShowImages={onShowImages} />

      {clarifying && findings && findings.questions.length > 0 ? (
        <PanelSection title={t.panel.stillNeeded}>
          <div className="rounded-xl border border-warning-border bg-warning-soft p-3.5">
            <ul className="flex flex-col gap-2">
              {findings.questions.map((question) => (
                <li key={question} className="flex gap-2 text-[13px] leading-relaxed text-foreground">
                  <CircleHelpIcon className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                  <span>{question}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2.5 text-[12px] text-warning">{t.panel.stillNeededHint}</p>
          </div>
        </PanelSection>
      ) : null}

      {chosen ? (
        <ChosenCodeCard candidate={chosen} box31={<Box31Block detail={detail} code={chosen.code} readOnly={readOnly} className="border-t px-4 py-2.5" />} />
      ) : null}

      {findings?.phase === "rejected" ? (
        <p className="rounded-xl border bg-muted/50 px-3.5 py-3 text-[13px] text-muted-foreground">{t.panel.rejected}</p>
      ) : null}

      {!chosen && findings?.phase === "classified" ? (
        <p className="rounded-xl border bg-muted/50 px-3.5 py-3 text-[13px] text-muted-foreground">{t.panel.noChosen}</p>
      ) : null}

      {open.length > 0 ? (
        <PanelSection title={t.panel.possibleCodes}>
          {clarifying ? <p className="-mt-1 text-[12.5px] text-muted-foreground">{t.panel.possibleCodesHint}</p> : null}
          <ul className="flex flex-col gap-2">
            {open.map((candidate) => (
              <OpenCodeCard
                key={candidate.code}
                candidate={candidate}
                box31={<Box31Block detail={detail} code={candidate.code} readOnly={readOnly} className="-mx-3.5 mt-0.5 border-t px-3.5 pt-2.5" />}
              />
            ))}
          </ul>
        </PanelSection>
      ) : null}

      {chosen || detail.session.declaredCode || clarifying ? <DeclaredCodeSection chosenCode={chosen?.code ?? null} /> : null}

      {chosen ? <FeedbackSection sessionId={detail.session.id} feedback={detail.feedback} readOnly={readOnly} /> : null}

      <ComparedCodes candidates={related} declaredCode={detail.session.declaredCode} />
    </div>
  );
}
